'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { PLANNER_HREF } from '../brand';
import { liveCabinetCatalogue, type CabinetCatalogue } from './cabinetCatalogue';
import { liveCabinetBasket, liveCabinetPricing, standardCabinets } from './cabinetOrders';
import type { CabinetOrderSpec, CabinetPricing } from './cabinetPricing';
import { liveDoorCatalogue, type DoorCatalogueSource } from './doorCatalogue';
import { liveDoorBasket, liveDoorPricing } from './doorOrders';
import type { DoorOrderSpec, DoorPricing } from './doorPricing';
import { accountPriceRequests, type PriceRequests } from './priceRequests';
import { accountSavedDesigns, type SavedDesigns } from './savedDesigns';
import { liveTradeJobs } from './liveTradeJobs';
import type { TradeJobs } from './tradeJobs';

/**
 * The data every Spatial builder and account page reads, behind ports.
 *
 * <p>Every port is the shop's real data or it is absent. The defaults below
 * read the live shop through the storefront's own API routes. A port with no
 * adapter yet is left undefined, and the block that needs it says the feature
 * is not open; nothing is ever invented to fill the gap (owner, 2026-09-28).
 *
 * <p>Doors are live: the catalogue is the shop's published door ranges, and
 * the server prices a spec and adds it to the basket (plan F1–F3). A shop that
 * has published no door ranges loads an empty catalogue, and the door builder
 * says ordering is not open.
 *
 * <p>Cabinets from the made-to-measure tab are priced and sold only when each
 * is a standard unit left as it is (`cabinetOrders.ts`); a modified cabinet
 * goes to `priceRequests`.
 *
 * <p>Fitting jobs are live: `tradeJobs` reads the signed-in fitter's account and
 * jobs through `/api/storefront/account/me` and `/api/storefront/installers/me/jobs/**`
 * (`liveTradeJobs.ts`).
 *
 * Tests supply their own adapters through {@link SpatialPortsProvider}.
 */
export interface SpatialPorts {
  cabinetCatalogue: CabinetCatalogue;
  savedDesigns: SavedDesigns;
  doorCatalogue?: DoorCatalogueSource;
  doorPricing?: DoorPricing;
  doorBasket?: DoorBasket;
  cabinetPricing?: CabinetPricing;
  cabinetBasket?: CabinetBasket;
  tradeJobs?: TradeJobs;
  /** Made-to-measure goods the shop prices by hand: send a request, see its quote, accept it. */
  priceRequests?: PriceRequests;
}

export interface DoorBasket {
  add(spec: DoorOrderSpec): Promise<void>;
}

export interface CabinetBasket {
  add(spec: CabinetOrderSpec): Promise<void>;
}

let live: SpatialPorts | null = null;

function livePorts(): SpatialPorts {
  if (!live) {
    const cabinetCatalogue = liveCabinetCatalogue();
    const doorPricing = liveDoorPricing();
    const doorBasket = liveDoorBasket();
    const standard = standardCabinets(cabinetCatalogue);
    live = {
      cabinetCatalogue,
      savedDesigns: accountSavedDesigns(PLANNER_HREF),
      doorCatalogue: liveDoorCatalogue(),
      doorPricing,
      doorBasket,
      cabinetPricing: liveCabinetPricing(standard, doorPricing),
      cabinetBasket: liveCabinetBasket(standard, doorBasket),
      priceRequests: accountPriceRequests(),
      tradeJobs: liveTradeJobs(),
    };
  }
  return live;
}

const PortsContext = createContext<Partial<SpatialPorts> | null>(null);

/** Supplies adapters; anything not given falls back to the live one, if there is one. */
export function SpatialPortsProvider({ ports, children }: { ports: Partial<SpatialPorts>; children: ReactNode }) {
  return <PortsContext.Provider value={ports}>{children}</PortsContext.Provider>;
}

export function useSpatialPorts(): SpatialPorts {
  const given = useContext(PortsContext);
  return useMemo(() => ({ ...livePorts(), ...(given ?? {}) }), [given]);
}
